import { Link } from "react-router-dom";
import { Formik } from "formik";
import { Loader2Icon } from "lucide-react";
import * as Yup from "yup";
import { Dropzone } from "@/components";
import { Button, buttonVariants } from "@/components/ui/button";
import { DialogClose } from "@/components/ui/dialog";
import { showSuccessToast } from "@/lib/toast";
import { addBulkTeachers } from "@/api/admin";
import { useMutation, useQueryClient } from "@tanstack/react-query";

type AddMultipleInstructorsProps = {
  onClose?: () => void
};

const AddMultipleInstructors = ({ onClose }: AddMultipleInstructorsProps) => {
  const queryClient = useQueryClient();
  const initialValues = { teachers: null };

  const validationSchema = Yup.object().shape({
    teachers: Yup.mixed()
      .required("File is required")
      .test("teachers", "Only Excel files are allowed", (value) => {
        if (value) {
          const file = value as File;
          const supportedFormats = ["xls", "xlsx"];

          return supportedFormats.includes(
            file?.name?.split(".").pop()!.toLowerCase(),
          );
        }
        return true;
      }),
  });

  const { mutate, isLoading } = useMutation({
    mutationFn: addBulkTeachers,
    onSuccess: () => {
      showSuccessToast("Teachers added successfully");
      queryClient.invalidateQueries({ queryKey: ["teachers"] });
      onClose?.();
    },
  });

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values) => mutate(values)}
      validationSchema={validationSchema}
    >
      {({ handleSubmit }) => (
        <form
          className="flex flex-col justify-between gap-6 pb-8"
          onSubmit={handleSubmit}
        >
          <Link
            to="/sample-instructors.xlsx"
            target="_blank"
            download
            className="mt-2 text-sm font-semibold w-fit text-primary"
          >
            Download Sample Excel File
          </Link>

          <Dropzone name="teachers" />

          <div className="flex items-center justify-end w-full gap-2">
            <DialogClose
              type="button"
              className={buttonVariants({
                variant: "outline",
                className: "h-8.5",
              })}
            >
              Cancel
            </DialogClose>
            <Button type="submit" className="h-8.5 min-w-[100px]">
              {isLoading ? <Loader2Icon className="animate-spin" /> : "Save"}
            </Button>
          </div>
        </form>
      )}
    </Formik>
  );
};

export default AddMultipleInstructors;
